// src/components/Navbar/Navbar.tsx
import React, { useState, useEffect, useRef, useCallback } from "react"; // Add useCallback
import { ChevronDown } from "lucide-react";

import logoWhite from "../../assets/img/logo.png";
import logoBlack from "../../assets/img/logo-black.png";

interface NavbarProps {
  onHeightChange?: (height: number) => void;
}

const Navbar: React.FC<NavbarProps> = ({ onHeightChange }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] =
    useState<boolean>(false);
  const [scrolled, setScrolled] = useState<boolean>(false);
  const navbarRef = useRef<HTMLElement>(null);
  // Removed navbarHeight state from Navbar, it will be managed by the parent via onHeightChange

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const toggleServicesDropdown = () =>
    setIsServicesDropdownOpen(!isServicesDropdownOpen);

  // Memoize the height calculation function to prevent unnecessary re-renders
  const calculateAndReportHeight = useCallback(() => {
    if (navbarRef.current && onHeightChange) {
      const newHeight = navbarRef.current.offsetHeight;
      onHeightChange(newHeight);
    }
  }, [onHeightChange]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Recalculate height on component mount/update and when mobile menu changes
    calculateAndReportHeight();

    // Add a resize observer to detect height changes due to content or browser resize
    // This is more robust than just relying on isMobileMenuOpen or scrolled state
    const resizeObserver = new ResizeObserver(() => {
      calculateAndReportHeight();
    });

    if (navbarRef.current) {
      resizeObserver.observe(navbarRef.current);
    }

    return () => {
      if (navbarRef.current) {
        resizeObserver.unobserve(navbarRef.current);
      }
    };
  }, [isMobileMenuOpen, scrolled, calculateAndReportHeight]); // Dependencies for recalculation

  const scrollToSection = (id: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    const element = document.getElementById(id);
    if (element) {
      // Get the *current* navbar height from its actual DOM size
      const currentNavbarHeight = navbarRef.current
        ? navbarRef.current.offsetHeight
        : 0;
      window.scrollTo({
        top: element.offsetTop - currentNavbarHeight, // Use current height for scrolling
        behavior: "smooth",
      });
      setIsMobileMenuOpen(false);
      setIsServicesDropdownOpen(false);
    }
  };

  const navLinkClasses = `nav-item block font-medium py-2 px-3 transition-colors duration-200`;

  return (
    <nav
      ref={navbarRef}
      className={`
        fixed w-full z-[999] transition-all duration-400 ease-in-out
        ${
          scrolled
            ? `bg-white shadow-lg text-gray-800`
            : `bg-gradient-navbar text-white`
        }
      `}
    >
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        {/* Logo and Tagline */}
        <a
          href="/#home-hero"
          onClick={(e) => scrollToSection("home-hero", e)}
          className="flex-shrink-0 flex flex-col items-center"
        >
          <img
            src={scrolled ? logoWhite : logoWhite}
            alt="logo"
            className="h-10 w-auto"
          />
          <span
            className={`${scrolled ? "text-black" : "text-white"} text-xs mt-1`}
          >
            For the next leap
          </span>
        </a>

        {/* Mobile menu toggle (Hamburger) */}
        <button
          className={`md:hidden focus:outline-none ${
            scrolled ? "text-gray-800" : "text-white"
          }`}
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation"
        >
          <svg
            className="w-8 h-8"
            fill="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            {isMobileMenuOpen ? (
              <path
                fillRule="evenodd"
                d="M6 18L18 6M6 6l12 12"
                clipRule="evenodd"
              />
            ) : (
              <path
                fillRule="evenodd"
                d="M4 6h16M4 12h16M4 18h16"
                clipRule="evenodd"
              />
            )}
          </svg>
        </button>

        {/* Desktop menu */}
        <div className={`hidden md:flex items-center space-x-8`}>
          <ul className="flex space-x-6">
            <li>
              <a
                href="#home-hero"
                onClick={(e) => scrollToSection("home-hero", e)}
                className={`${navLinkClasses} ${
                  scrolled
                    ? "text-gray-700 hover:text-blue-400"
                    : "text-white hover:text-blue-400"
                }`}
              >
                Home
              </a>
            </li>
            <li className="relative">
              <button
                onClick={toggleServicesDropdown}
                className={`${navLinkClasses} flex items-center focus:outline-none ${
                  scrolled
                    ? "text-gray-700 hover:text-blue-400"
                    : "text-white hover:text-blue-400"
                }`}
              >
                Services{" "}
                <ChevronDown
                  className={`ml-1 h-4 w-4 transition-transform duration-200 ${
                    isServicesDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {isServicesDropdownOpen && (
                <ul className="absolute top-full left-0 mt-2 w-48 bg-white shadow-lg rounded-md overflow-hidden text-gray-800">
                  <li>
                    <a
                      href="#it-solutions"
                      onClick={(e) => scrollToSection("it-solutions", e)}
                      className="block px-4 py-2 hover:bg-gray-100"
                    >
                      IT Solutions
                    </a>
                  </li>
                  <li>
                    <a
                      href="#digital-marketing"
                      onClick={(e) => scrollToSection("digital-marketing", e)}
                      className="block px-4 py-2 hover:bg-gray-100"
                    >
                      Digital Marketing
                    </a>
                  </li>
                </ul>
              )}
            </li>
            <li>
              <a
                href="#aboutus"
                onClick={(e) => scrollToSection("aboutus", e)}
                className={`${navLinkClasses} ${
                  scrolled
                    ? "text-gray-700 hover:text-blue-400"
                    : "text-white hover:text-blue-400"
                }`}
              >
                About Us
              </a>
            </li>
            <li>
              <a
                href="#why-choose-us"
                onClick={(e) => scrollToSection("why-choose-us", e)}
                className={`${navLinkClasses} ${
                  scrolled
                    ? "text-gray-700 hover:text-blue-400"
                    : "text-white hover:text-blue-400"
                }`}
              >
                Why Choose Us?
              </a>
            </li>
            <li>
              <a
                href="#how-we-work"
                onClick={(e) => scrollToSection("how-we-work", e)}
                className={`${navLinkClasses} ${
                  scrolled
                    ? "text-gray-700 hover:text-blue-400"
                    : "text-white hover:text-blue-400"
                }`}
              >
                How We Work
              </a>
            </li>
            <li>
              <a
                href="#testimonials"
                onClick={(e) => scrollToSection("testimonials", e)}
                className={`${navLinkClasses} ${
                  scrolled
                    ? "text-gray-700 hover:text-blue-400"
                    : "text-white hover:text-blue-400"
                }`}
              >
                Testimonial
              </a>
            </li>
            <li>
              <a
                href="/contactus"
                target="_blank" // Opens in a new tab
                rel="noopener noreferrer" // Security best practice for target="_blank"
                className={`${navLinkClasses} ${
                  scrolled
                    ? "text-gray-700 hover:text-blue-400"
                    : "text-white hover:text-blue-400"
                }`}
                id="lnkContactUs"
              >
                Contact Us
              </a>
            </li>
          </ul>
          <div className="other-option">
            <a
              className="default-btn bg-custom-purple text-white py-2 px-6 rounded-full hover:opacity-90 transition-opacity duration-300 shadow-lg"
              href="mailto:demo@example.com"
            >
              Get IT Support <span className="ml-2">&#8594;</span>
            </a>
          </div>
        </div>

        {/* Mobile menu (overlay/collapsed) */}
        {isMobileMenuOpen && (
          <div
            className={`
              md:hidden fixed left-0 w-full bg-white shadow-lg py-4 z-[900]
              transition-all duration-300 ease-in-out text-gray-800
            `}
            style={{
              top: `${
                navbarRef.current ? navbarRef.current.offsetHeight : 0
              }px`,
              height: `calc(100vh - ${
                navbarRef.current ? navbarRef.current.offsetHeight : 0
              }px)`,
              overflowY: "auto",
            }}
          >
            <ul className="flex flex-col space-y-2 px-4">
              <li>
                <a
                  href="#home-hero"
                  onClick={(e) => scrollToSection("home-hero", e)}
                  className="block py-2 px-4 hover:bg-gray-100 rounded-md text-gray-800"
                >
                  Home
                </a>
              </li>
              <li className="relative">
                <button
                  onClick={toggleServicesDropdown}
                  className="block py-2 px-4 hover:bg-gray-100 rounded-md text-gray-800 flex items-center justify-between w-full focus:outline-none"
                >
                  Services{" "}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${
                      isServicesDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isServicesDropdownOpen && (
                  <ul className="pl-4 mt-2 space-y-1">
                    <li>
                      <a
                        href="#it-solutions"
                        onClick={(e) => scrollToSection("it-solutions", e)}
                        className="block px-4 py-2 hover:bg-gray-100"
                      >
                        IT Solutions
                      </a>
                    </li>
                    <li>
                      <a
                        href="#digital-marketing"
                        onClick={(e) => scrollToSection("digital-marketing", e)}
                        className="block px-4 py-2 hover:bg-gray-100"
                      >
                        Digital Marketing
                      </a>
                    </li>
                  </ul>
                )}
              </li>
              <li>
                <a
                  href="#about-us"
                  onClick={(e) => scrollToSection("about-us", e)}
                  className="block py-2 px-4 hover:bg-gray-100 rounded-md text-gray-800"
                >
                  About Us
                </a>
              </li>
              <li>
                <a
                  href="#why-choose-us"
                  onClick={(e) => scrollToSection("why-choose-us", e)}
                  className="block py-2 px-4 hover:bg-gray-100 rounded-md text-gray-800"
                >
                  Why Choose Us?
                </a>
              </li>
              <li>
                <a
                  href="#how-we-work"
                  onClick={(e) => scrollToSection("how-we-work", e)}
                  className="block py-2 px-4 hover:bg-gray-100 rounded-md text-gray-800"
                >
                  How We Work
                </a>
              </li>
              <li>
                <a
                  href="#testimonials"
                  onClick={(e) => scrollToSection("testimonials", e)}
                  className="block py-2 px-4 hover:bg-gray-100 rounded-md text-gray-800"
                >
                  Testimonial
                </a>
              </li>
              <li>
                <a
                  href="/contactus"
                  target="_blank" // Opens in a new tab
                  rel="noopener noreferrer" // Security best practice for target="_blank"
                  className="block py-2 px-4 hover:bg-gray-100 rounded-md text-gray-800"
                  id="lnkContactUs"
                >
                  Contact Us
                </a>
              </li>
            </ul>
            <div className="other-option px-4 mt-4">
              <a
                className="default-btn bg-custom-purple text-white py-2 px-6 rounded-full hover:opacity-90 transition-opacity duration-300 w-full text-center block"
                href="mailto:demo@example.com"
              >
                Get IT Support <span className="ml-2">&#8594;</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
